import {
  createDraft,
  deleteVaultFile,
  duplicateDraft,
  finalizeDraft,
  getDraft,
  searchDrafts,
  updateDraft,
  updateVaultFileRevision,
  type ContractGenerationDraftCreateRequest,
  type ContractGenerationDraftInDb,
  type ContractGenerationDraftSearchResult,
  type ContractGenerationDraftUpdateRequest,
  type ContractGenerationFinalizeResponse,
  type ContractGenerationFinalizeSigning,
  type FilterableSearchQuery,
} from '@/api';
import { extractErrorDetail } from '@/utils/errorHandler';
import { buildRenderError, RenderError, type SdkLikeResponse } from './renderService';

/**
 * A document draft is the Word-style "open file" of contract templating: one
 * document a user is filling in from a template. It is a vault file whose
 * current revision is templated (rendered from the template and the values on
 * every read), so its `uid` is the vault file's and `revision_uid` its
 * revision's. The wizard creates it on the first entry, autosaves the values
 * into it, and `finalize` is the "Save As" that stores the rendered .docx as
 * a new revision of the same file and makes it a contract. From then on it is
 * a document, not a draft: the draft routes answer 404 for it.
 *
 * Normal vault permissions apply. Name, folder and deletion go through the
 * vault routes, like any file.
 */

export interface DraftSearchQuery {
  text?: string;
  page?: number;
  pageSize?: number;
  sortKey?: string;
  sortDirection?: 'asc' | 'desc';
}

/** The backend caps a page at 50. */
export const DRAFT_PAGE_SIZES = [10, 25, 50];

function buildSearchBody(query: DraftSearchQuery): FilterableSearchQuery {
  const pageSize = query.pageSize ?? 25;
  const page = query.page ?? 1;
  return {
    text: query.text?.trim() ?? '',
    limit: pageSize,
    offset: (page - 1) * pageSize,
    sort_key: query.sortKey ?? 'created_at',
    sort_direction: query.sortDirection ?? 'desc',
    column_filters: {},
  };
}

export const DraftsService = {
  async search(query: DraftSearchQuery = {}): Promise<ContractGenerationDraftSearchResult> {
    try {
      const response = await searchDrafts({ body: buildSearchBody(query) });
      if (!response.data) {
        throw new Error('Failed to search drafts');
      }
      return response.data;
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to load drafts.'));
    }
  },

  async get(uid: string): Promise<ContractGenerationDraftInDb> {
    try {
      const response = await getDraft({ path: { uid } });
      if (!response.data) {
        throw new Error('Failed to fetch draft');
      }
      return response.data;
    } catch (error) {
      // The cause keeps the status: a 404 is a draft saved (or deleted) since.
      throw new Error(extractErrorDetail(error, 'Failed to load draft.'), { cause: error });
    }
  },

  /**
   * Start a draft. The template has to be usable right now — the backend
   * answers 409 while it is still validating and 422 when it is broken — so
   * the failure is a typed `RenderError` the wizard can explain.
   */
  async create(
    payload: ContractGenerationDraftCreateRequest
  ): Promise<ContractGenerationDraftInDb> {
    const response = (await createDraft({ body: payload, throwOnError: false })) as SdkLikeResponse;
    if (response.error) {
      throw buildRenderError(response, 'Failed to start the draft.');
    }
    if (!response.data) {
      throw new RenderError('unknown', 'Failed to start the draft.');
    }
    return response.data as ContractGenerationDraftInDb;
  },

  /**
   * The autosave call. Every field is optional; `form_data` replaces the
   * stored values wholesale. Only a draft accepts it — a refusal here usually
   * means the draft was saved in another tab, and the caller should reload it
   * rather than keep retrying.
   */
  async update(
    uid: string,
    payload: ContractGenerationDraftUpdateRequest
  ): Promise<ContractGenerationDraftInDb> {
    const response = (await updateDraft({
      path: { uid },
      body: payload,
      throwOnError: false,
    })) as SdkLikeResponse;
    if (response.error) {
      throw buildRenderError(response, 'Failed to save the draft.');
    }
    if (!response.data) {
      throw new RenderError('unknown', 'Failed to save the draft.');
    }
    return response.data as ContractGenerationDraftInDb;
  },

  /** Discard a draft: it is a vault file, deleted like one. */
  async delete(uid: string): Promise<void> {
    try {
      await deleteVaultFile({ path: { uid } });
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to delete draft.'));
    }
  },

  /** Rename a draft — its templated revision's file name. Returns the name the backend kept. */
  async rename(draft: ContractGenerationDraftInDb, fileName: string): Promise<string> {
    try {
      const response = await updateVaultFileRevision({
        path: { uid: draft.uid, revision_uid: draft.revision_uid },
        body: { file_name: fileName },
      });
      return response.data?.file_name ?? fileName;
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to rename the draft.'));
    }
  },

  /**
   * Start a new draft from this one's values, in the same folder. Works on a
   * saved document too. The copy's name is made unique by the backend.
   */
  async duplicate(uid: string): Promise<ContractGenerationDraftInDb> {
    try {
      const response = await duplicateDraft({ path: { uid } });
      if (!response.data) {
        throw new Error('Failed to duplicate draft');
      }
      return response.data;
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to duplicate draft.'));
    }
  },

  /**
   * The "Save As": store the draft's rendered values as a new revision of the
   * same file and make it a contract — with `signing`, also send it for
   * signing, the template's signing slots' parties as signers. The backend
   * renders the stored values, so the caller must flush any pending autosave
   * first. Afterwards the draft routes answer 404 for it.
   */
  async finalize(
    uid: string,
    signing: ContractGenerationFinalizeSigning | null = null
  ): Promise<ContractGenerationFinalizeResponse> {
    const response = (await finalizeDraft({
      path: { uid },
      body: { signing },
      throwOnError: false,
    })) as SdkLikeResponse;
    if (response.error) {
      throw buildRenderError(response, 'Failed to generate the document.');
    }
    if (!response.data) {
      throw new RenderError('unknown', 'Failed to generate the document.');
    }
    return response.data as ContractGenerationFinalizeResponse;
  },
};
